'use client';

import DollarCoinFilledSvg from '@/assets/svgs/dollar-coin-filled.svg';
import { useAddAccountCredit } from '@/containers/Accounts/api';
import { TRANSACTION_TYPES } from '@/containers/Accounts/constants';
import AddAccountTransactionValidationSchema from '@/containers/Modals/AddAccountTransactionModal/validators';
import { Modals } from '@/containers/Modals/all-modals';
import ModalBody from '@/containers/Modals/ModalBody';
import ModalContainer from '@/containers/Modals/ModalContainer';
import ModalHeader from '@/containers/Modals/ModalHeader';
import { AddAccountTransactionModalParams, ModalProps } from '@/containers/Modals/types';
import useModals from '@/containers/Modals/useModals';
import { useToast } from '@/containers/ToastMessage';
import { InputContainer } from '@/shared/components/FormInputs';
import CurrencyInput from '@/shared/components/FormInputs/CurrencyInput/CurrencyInput';
import useUser from '@/shared/hooks/useUser';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useEffect, useMemo } from 'react';

interface AddAccountTransactionModalProps extends ModalProps {
  params?: AddAccountTransactionModalParams;
}

interface TransactionFormValues {
  payment_type: string;
  credit_amount: string;
  payment_by: string;
  reference_number: string;
  notes: string;
}

const modalName = Modals.AddAccountTransactionModal;

const AddAccountTransactionModal = ({
  className,
  params,
  variant = 'narrow',
}: AddAccountTransactionModalProps) => {
  const { isModalOpen, onClose } = useModals({ modalName });
  const { showError, showSuccess } = useToast();
  const { appUser } = useUser();

  const accountId = params?.accountId || '';

  // Get user's full name for pre-filling
  const userFullName = useMemo(() => {
    if (!appUser) return '';
    return `${appUser.first_name} ${appUser.last_name}`.trim();
  }, [appUser]);

  const {
    mutate: addCredit,
    isPending: isAddingCredit,
    error: addCreditError,
    isSuccess,
    reset: resetMutation,
  } = useAddAccountCredit();

  const formik = useFormik<TransactionFormValues>({
    validateOnBlur: true,
    initialValues: {
      payment_type: '',
      credit_amount: '',
      payment_by: userFullName,
      reference_number: '',
      notes: '',
    },
    validationSchema: AddAccountTransactionValidationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        addCredit({
          accountId,
          payload: {
            credit_amount: parseFloat(values.credit_amount),
            notes: values.notes,
            payment_by: values.payment_by,
            payment_type: values.payment_type,
            reference_number: values.reference_number,
          },
        });
      } catch (e) {
        setSubmitting(false);
        console.error('Error adding transaction:', e);
        showError('Failed to add transaction. Please try again.');
      }
    },
  });

  const isLoading = formik.isSubmitting || isAddingCredit;
  const isFormValid = formik.isValid && formik.dirty;

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      showSuccess('Transaction added successfully');
      formik.resetForm();
      resetMutation();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, onClose, showSuccess]);

  // Handle error
  useEffect(() => {
    if (addCreditError) {
      console.error('Error adding credit:', addCreditError);
      showError('Failed to add transaction. Please try again.');
      formik.setSubmitting(false);
      resetMutation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCreditError, showError]);

  if (!isModalOpen) return null;

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}>
        <form onSubmit={formik.handleSubmit} className="h-full">
          <div className="flex h-full flex-col">
            {/* Header */}
            <ModalHeader
              modalName={modalName}
              closeAction="state"
              icon={<DollarCoinFilledSvg className="h-5 w-5" />}
              title="Add Transaction"
            />

            {/* Body */}
            <ModalBody className="px-5 !py-0">
              {/* Spacing for consistency */}
              <div className="h-6" />

              {/* Form */}
              <div className="form py-2">
                <div
                  className={classNames('flex flex-col gap-4', {
                    'disabled-container': isLoading,
                  })}>
                  {/* Row 1: Type and Amount */}
                  <div className="grid grid-cols-2 gap-4">
                    <InputContainer
                      label="Type"
                      error={formik.errors.payment_type}
                      isTouched={formik.touched.payment_type}>
                      <select
                        className="focus:input-primary active:input-primary select select-bordered w-full text-sm"
                        name="payment_type"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.payment_type}>
                        <option value="">Select type</option>
                        {Object.entries(TRANSACTION_TYPES).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </InputContainer>

                    <CurrencyInput
                      label="Amount"
                      value={formik.values.credit_amount}
                      onChange={(value) => formik.setFieldValue('credit_amount', value)}
                      error={
                        formik.touched.credit_amount
                          ? formik.errors.credit_amount
                          : undefined
                      }
                      placeholder="0.00"
                      step="0.01"
                      inputClassName="text-sm"
                    />
                  </div>

                  {/* Row 2: Applied By and Reference */}
                  <div className="grid grid-cols-2 gap-4">
                    <InputContainer
                      label="Applied By"
                      error={formik.errors.payment_by}
                      isTouched={formik.touched.payment_by}>
                      <input
                        type="text"
                        name="payment_by"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.payment_by}
                        placeholder="Name"
                        className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
                      />
                    </InputContainer>

                    <InputContainer
                      label="Reference"
                      error={formik.errors.reference_number}
                      isTouched={formik.touched.reference_number}>
                      <input
                        type="text"
                        name="reference_number"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.reference_number}
                        placeholder="Reference number"
                        className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
                      />
                    </InputContainer>
                  </div>

                  {/* Row 3: Notes (full width) */}
                  <InputContainer
                    label="Notes"
                    error={formik.errors.notes}
                    isTouched={formik.touched.notes}>
                    <input
                      type="text"
                      name="notes"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.notes}
                      placeholder="Add notes"
                      className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
                    />
                  </InputContainer>

                  {/* Submit Button */}
                  <button
                    className="btn btn-primary mt-5 mb-3 w-full"
                    type="submit"
                    disabled={!isFormValid || isLoading}>
                    {isLoading ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              </div>
            </ModalBody>
          </div>
        </form>
      </ModalContainer>
    </div>
  );
};

export default AddAccountTransactionModal;
